import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { cn } from "@omni/utils/styles";
import { memo } from "react";
import { useSelector } from "react-redux";

import { ContentSearchResultsComponent } from "@/components/Search/SearchResults/contentSearchResults.component";
import { useSearchResultsContext } from "@/components/Search/SearchResults/context/searchResults.context";
import { ProductSearchResultsComponent } from "@/components/Search/SearchResults/productSearchResults.component";
import { NumberOfHits } from "@/components/Search/SearchResults/StateComponents/NumberOfHits/numberOfHits.container";
import * as SearchSelectors from "@/redux/modules/value/search/search.selectors";
import * as ScrollPositionService from "@/services/scrollPosition.service";
import { isPageTemplate } from "@/utils/pageTemplate.utils";
import RenderCallback from "@/utils/renderCallback";
import { SearchComponent } from "@/utils/search/types/searchComponent";

import { SearchTitle } from "./components/SearchTitle/searchTitle.container";

const SearchResultsComponentBase = () => {
    const { showFacets } = useSearchResultsContext();

    const onSearchPage = isPageTemplate(PageTemplate.Search);

    const hasResultComponents = useSelector(
        SearchSelectors.hasAnyComponents([
            SearchComponent.PRODUCT_NUMBER_OF_HITS,
            SearchComponent.CONTENT_NUMBER_OF_HITS
        ])
    );

    const hasSelectedProductFacets = useSelector(
        SearchSelectors.hasSelectedFacets(SearchComponent.PRODUCT_FACETS)
    );

    const hasProductResults = useSelector(
        SearchSelectors.hasResults(SearchComponent.PRODUCT_NUMBER_OF_HITS)
    );
    const hasContentResults = useSelector(
        SearchSelectors.hasResults(SearchComponent.CONTENT_NUMBER_OF_HITS)
    );

    const afterRender = () => {
        ScrollPositionService.scrollToLastPosition();
    };

    const hasAnyResults = hasProductResults || hasContentResults;

    const renderProductResults = hasProductResults || hasSelectedProductFacets;
    const renderContentResults = hasContentResults && onSearchPage;

    if (!hasResultComponents || !hasAnyResults) return null;

    return (
        <article
            className={cn(
                "tw:min-h-[calc(100dvh-var(--spacing-header-height-mobile))]",
                "tw:lg:min-h-[calc(100dvh-var(--spacing-header-height-desktop))]"
            )}
        >
            {onSearchPage && <SearchTitle />}
            {showFacets && <NumberOfHits />}
            <section
                className={cn(
                    "tw:relative tw:grid tw:gap-8 tw:grid-cols-[1fr]",
                    "tw:lg:grid-cols-[2fr_8fr]",
                    {
                        "tw:lg:grid-cols-[1fr]": !showFacets
                    }
                )}
            >
                <RenderCallback callback={afterRender}>
                    {renderProductResults && <ProductSearchResultsComponent />}
                    {renderContentResults && (
                        <ContentSearchResultsComponent
                            expanded={!renderProductResults}
                        />
                    )}
                </RenderCallback>
            </section>
        </article>
    );
};

export const SearchResultsComponent = memo(SearchResultsComponentBase);
